import type { IProfile } from '@/interfaces/agency/clients';
import type {
  IActivityLogDetails,
  IActivityLogResponse,
  IAddress,
  IAdministrationSchedule,
  ICommonEntity,
  IJobType,
  IProfileType,
  IUpdateBulkProfileArea,
  IUpdateBulkProfileStatus,
  IUpdateProfileStatus
} from '@/interfaces/agency/common';
import axios from '@axios';

export default {
  // Profile Api's
  fetchProfileAddress(profileId: number, hideLoader = false) {
    return axios.get<Array<IAddress>>(`profile-addresses/profiles/${profileId}`, {
      headers: hideLoader ? { 'x-hide-loader': true } : {}
    });
  },

  addProfileAddress(profileId: number, payload: Partial<IAddress>) {
    return axios.post(`profile-addresses/profiles/${profileId}`, payload);
  },

  updateProfileAddress(payload: Partial<IAddress>) {
    return axios.patch(`profile-addresses/${payload.id}`, payload);
  },

  updateProfile(profileId: number, payload: Partial<IProfile>) {
    return axios.patch(`profiles/${profileId}`, payload);
  },

  updateProfileStatus(profileId: number, payload: IUpdateProfileStatus) {
    return axios.patch(`profiles/${profileId}`, payload);
  },

  updateBulkProfileStatus(payload: IUpdateBulkProfileStatus) {
    return axios.patch('profiles/bulk/status', payload);
  },

  updateBulkProfileArea(payload: IUpdateBulkProfileArea) {
    return axios.patch<{
      succeeded: { profileId: number; name: string }[];
      failed: { profileId: number; name: string; reason: string }[];
      totalProcessed: number;
      totalSucceeded: number;
      totalFailed: number;
    }>('profile-areas/bulk', payload);
  },

  uploadFile(profileId: number, file: File) {
    const formData = new FormData();

    formData.append('file', file);

    return axios.patch(`profiles/${profileId}/image`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    });
  },

  getDays() {
    return axios.get<Array<ICommonEntity>>('days');
  },

  getRoles() {
    return axios.get<Array<IProfileType>>('roles');
  },

  // todo
  async getAdministrationSchedule() {
    return await axios.get<Array<IAdministrationSchedule>>('/administration_schedule');
  },

  profileType(type: string) {
    return axios.get<IProfileType>(`/profiletype/by/${type}`);
  },

  async getJobTypes(status: boolean) {
    return await axios.get<Array<IJobType>>(`/job_type/by/${status}`);
  },

  // Bulk Resend Login
  bulkResendLogin(payload: { profileIds: number[] }) {
    return axios.post('profiles/bulk-resend-login', payload);
  },

  // Activity Log
  fetchActivityLog(params: object, hideLoader = false) {
    return axios.get<IActivityLogResponse>(`/activity-logs`, {
      params,
      headers: hideLoader ? { 'x-hide-loader': true } : {}
    });
  },

  fetchClientActivityLog(params: object) {
    return axios.get<IActivityLogResponse>(`/clients/client-activity-logs`, { params });
  },

  fetchCarerActivityLog(params: object, hideLoader = false) {
    return axios.get<IActivityLogResponse>(`/carers/carer-activity-logs`, {
      params,
      headers: hideLoader ? { 'x-hide-loader': true } : {}
    });
  },

  fetchActivityLogById(id: number) {
    return axios.get<IActivityLogDetails>(`/activity-logs/view/${id}`);
  }
};
